/**
 * TV1-1930 (S15) — pure value/delta/format logic for the KPI (big-number) widget.
 *
 * DOM-free on purpose: the whole point of a KPI is the NUMBER, so the number's
 * semantics are testable without a renderer.
 *
 * ## The rule this module exists to obey: "unknown, never silently zero"
 *
 * (`marketplace-health/CONTEXT.md:34` — a general epistemic rule of this
 * project.) A KPI is the single most confidently-read element on a dashboard;
 * a fabricated `0` or a fabricated `+100%` is worse than an em-dash, because the
 * reader has no way to tell it apart from a real measurement. Four distinct
 * states get four distinct renderings, and every one of them is pinned in
 * `kpiData.test.ts`:
 *
 * | current | prior | value | delta |
 * |---|---|---|---|
 * | no rows | anything | **unknown** ("—") | **unknown** ("—") |
 * | rows | no rows | the value | **unknown** ("—") — NOT `+100%`, NOT `0%` |
 * | `> 0` | `0` | the value | **absolute only** — the % is undefined (÷0); never `∞` |
 * | `0` | `0` | `0` | **`0%`** — legitimately flat |
 *
 * ## Colour is opt-in (`goodDirection`, default `'neutral'`)
 *
 * Nothing in the domain knows a measure's polarity — Cube's meta carries
 * `format`, not "higher is better". For `returnRate` / `cancellationRate`,
 * **up is bad**. A default of `'up'` would paint a rising return-rate tile
 * confidently GREEN, and TV1-1932's seven-tile row will contain exactly such a
 * metric. So the default is `'neutral'`: arrow + number, no colour. Colour is
 * something the author opts into, per widget, having thought about it.
 * (Rejected: deriving polarity from the measure name — silent, and wrong on
 * `conversionRate`.)
 */

import { isCompareWidgetData, type CompareWidgetData, type CubeRow } from '../widgetData';

/** Rendered wherever a value or a delta is genuinely unknown. Never `0`, never blank. */
export const KPI_EMPTY = '—';

export type KpiFormat = 'number' | 'currency' | 'percent';

/** Which direction is "good" for this measure. `'neutral'` = no colour at all. */
export type KpiGoodDirection = 'up' | 'down' | 'neutral';

/**
 * The three genuinely different things a period-over-period delta can be.
 *
 * A discriminated union rather than `{ percent: number | null }` so that the
 * renderer CANNOT accidentally print a `0` for an unknown: it has to handle
 * `'unknown'` explicitly.
 */
export type KpiDelta =
  /** No prior measurement. Render "—". This is NOT `+100%` and NOT `0%`. */
  | { kind: 'unknown' }
  /** Prior was exactly 0, so the percentage is undefined (÷0). Absolute only — never `∞`. */
  | { kind: 'absolute'; absolute: number }
  /** Both periods measured. */
  | { kind: 'full'; absolute: number; percent: number };

export type KpiData = {
  /** `null` when the current period returned no usable rows — unknown, not 0. */
  value: number | null;
  /**
   * PREFACTOR-grammar — the raw measure value AS TEXT, for a KPI whose member is
   * not a number.
   *
   * MercadoLibre's seller reputation level is the motivating case: it is the
   * string `'5_green'`. `sumMeasure` does `Number('5_green')` -> `NaN` -> skips the
   * row -> returns `null`, so the tile rendered `"—"`: a widget that literally
   * could not display the value it was pointed at, and did so silently.
   *
   * Populated always; the renderer only USES it under `valueMode: 'text'`, so a
   * numeric KPI is unaffected.
   */
  textValue: string | null;
  delta: KpiDelta;
  /** `true` when the widget actually asked for a comparison (i.e. show the delta line at all). */
  hasCompare: boolean;
};

/**
 * Total a measure across the returned rows.
 *
 * A KPI query normally carries no dimensions, so Cube returns exactly one row —
 * but summing is both correct and robust if a dimension sneaks in.
 *
 * Returns `null` (unknown), NOT `0`, when there are no rows, or when the rows
 * carry no usable value for the measure. That distinction is the whole ballgame:
 * `0` means "we measured, and it was zero"; `null` means "we did not measure".
 * Cube sends numeric measures as STRINGS over the wire, so coerce before testing.
 */
export const sumMeasure = ( rows: CubeRow[] | null | undefined, measure: string ): number | null => {
  if ( !Array.isArray( rows ) || !rows.length || !measure ) return null;

  let sum = 0;
  let measured = false;

  for ( const row of rows ) {
    const raw = row[measure];
    if ( raw === null || raw === undefined || raw === '' ) continue;

    const numeric = typeof raw === 'number' ? raw : Number( raw );
    if ( Number.isNaN( numeric )) continue;

    sum += numeric;
    measured = true;
  }

  return measured ? sum : null;
};

/**
 * Compare two period totals.
 *
 * The percentage is relative to `Math.abs( prior )`, not `prior`. With a raw
 * `prior` a negative baseline flips the sign of the change: going from -100 to
 * -50 is an IMPROVEMENT of 50, but `50 / -100` reports it as `-50%`. Dividing by
 * the magnitude keeps the sign of the change, which is what the arrow and the
 * colour both key off.
 */
export const computeKpiDelta = ( current: number | null, prior: number | null ): KpiDelta => {
  // We never measured the current period (or the prior one) — there is nothing
  // to compare. Not zero. Not 100%.
  if ( current === null || prior === null ) return { kind: 'unknown' };

  const absolute = current - prior;

  if ( prior === 0 ) {
    // 0 -> 0 is a real, measured, flat result.
    if ( current === 0 ) return { kind: 'full', absolute: 0, percent: 0 };
    // 0 -> anything else is an infinite percentage. Show the absolute change and
    // suppress the percentage rather than rendering `∞` or a fake `+100%`.
    return { kind: 'absolute', absolute };
  }

  return { kind: 'full', absolute, percent: ( absolute / Math.abs( prior )) * 100 };
};

/** Whether a delta (or a value) should be painted good, bad, or left alone. */
export type KpiDeltaTone = 'positive' | 'negative' | 'neutral';

/**
 * PREFACTOR-grammar — a VALUE-band threshold, as opposed to the delta rule below.
 *
 * ## The gap this closes
 *
 * KPI colour was, until now, a **delta** rule only (`deltaTone`), and it was
 * rendered exclusively inside `{ hasCompare ? … : null }`. Two consequences:
 *
 *  - **the value itself was never coloured** — "claim rate is 2.1%, and the bar is
 *    3%, so this is bad" was inexpressible;
 *  - **no compare meant no colour at all** — and a seller-reputation metric is a
 *    current snapshot with no history, so it can never have a compare.
 *
 * `KpiProps.tsx` said so outright: *"No target/threshold. Not in the AC; not built."*
 *
 * ## TWO bands, not three — and this is deliberate
 *
 * `good <= threshold < bad`. The numbers this was built for are MercadoLibre's
 * published green-tier BOUNDARIES (claims < 3%, cancellations < 3%, delayed
 * handling < 20%) — they answer "does this seller meet the bar?", and nothing
 * else. A green/amber/red band would need a second, middle boundary that ML does
 * not publish and that nobody at Teselly has defined; inventing one would be
 * dressing a guess up as a measurement.
 *
 * A middle band can be added later WITHOUT a schema change — `thresholds` rides on
 * `widgetComponentProps`, which is `additionalProperties: true`.
 *
 * `direction` says which side of the boundary is the good one:
 *  - `'below'` — lower is better (claim rate, cancellation rate, return rate);
 *  - `'above'` — higher is better (conversion rate, margin %).
 *
 * Exactly ON the threshold is GOOD (`<=` / `>=`) — a seller who is exactly at the
 * published bar meets it.
 */
export type KpiThresholds = {
  value: number;
  direction: 'above' | 'below';
};

export const isKpiThresholds = ( raw: unknown ): raw is KpiThresholds => {
  if ( typeof raw !== 'object' || raw === null ) return false;
  const candidate = raw as Record<string, unknown>;
  return typeof candidate.value === 'number'
    && Number.isFinite( candidate.value )
    && ( candidate.direction === 'above' || candidate.direction === 'below' );
};

/**
 * Paint the VALUE against its threshold band.
 *
 * Independent of `hasCompare` — that is the entire point. Returns `'neutral'` for
 * an unmeasured value (`null`): a tile with no data is not "bad", it is unknown,
 * and painting it red would be a fabricated measurement. Same rule as everywhere
 * else in this module.
 */
export const valueTone = (
  value: number | null | undefined,
  thresholds: KpiThresholds | undefined
): KpiDeltaTone => {
  if ( !thresholds || value === null || value === undefined || !Number.isFinite( value )) return 'neutral';

  const meetsBar = thresholds.direction === 'below'
    ? value <= thresholds.value
    : value >= thresholds.value;

  return meetsBar ? 'positive' : 'negative';
};

/**
 * TV1-1934 (S19) — the KPI link target, after scheme allow-listing.
 *
 * `'internal'` goes through react-router's `<Link>`; `'external'` becomes a plain
 * `<a>`. Anything that is not one of these two is NOT A LINK AT ALL — see
 * {@link sanitizeKpiHref}.
 */
export type KpiHref =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string };

/**
 * TV1-1934 (S19) — **STORED-XSS FIX.** Allow-list a KPI's `href` by its PARSED scheme.
 *
 * ## The hole this closes
 *
 * PREFACTOR-grammar added `kpi.href` and routed in-app paths (`/…`) through `<Link>`,
 * but let **everything else** fall through to a raw `<a href={href}>`. So a
 * `javascript:` URI typed into the manual widget builder executed on click — and
 * because **widget layouts are shared across every user in the account**, that is
 * STORED XSS, not self-XSS. (TV1-1938 closed only the LLM path, by withholding `href`
 * from the model-facing schema. The manual builder was still wide open.)
 *
 * ## Allow-list, not deny-list. Fail CLOSED.
 *
 * Permitted, and nothing else:
 *  - **in-app** — starts with a single `/`;
 *  - **`http:` / `https:`** — a real, absolute, external URL.
 *
 * A rejected href does not degrade to some other target and does not throw: the
 * caller renders the value as **plain text with no anchor**. A link that silently
 * points somewhere other than where its author meant is worse than no link.
 *
 * ## Why the scheme is PARSED, and never matched as a substring
 *
 * `startsWith( 'javascript:' )` is not a defence — the browser normalises a URL
 * before it resolves the scheme, so several strings that do NOT begin with
 * `javascript:` still execute as it:
 *
 *  - **`JaVaScRiPt:alert(1)`** — schemes are case-insensitive. `new URL()` lowercases
 *    `.protocol`, so the comparison is done on the normalised value.
 *  - **`java\tscript:alert(1)`** (also `\n`, `\r`) — browsers STRIP tab/LF/CR from a
 *    URL *anywhere in the string* before parsing. A check on the raw text is checking
 *    a string the browser will never see. We reject on any C0 control instead of
 *    stripping-then-trusting: a control character has no business in a hand-authored
 *    link, so its presence is itself the signal.
 *  - **`/\evil.com`** — for `http(s)` the WHATWG URL parser treats `\` as `/`, so this
 *    normalises to `//evil.com`: a protocol-relative jump to an attacker host wearing
 *    an in-app path's clothing. Backslashes are rejected outright.
 *  - **`//evil.com`** — protocol-relative. Its scheme IS `http(s)`, so a naive scheme
 *    allow-list waves it through, and `.startsWith( '/' )` mistakes it for in-app.
 *    Checked BEFORE the single-slash in-app branch.
 *
 * `data:` and `vbscript:` need no special case — they are simply not on the list.
 * That is the point of an allow-list: the next exotic scheme is already handled.
 */
export const sanitizeKpiHref = ( raw: unknown ): KpiHref | null => {
  if ( typeof raw !== 'string' ) return null;

  const href = raw.trim();
  if ( !href ) return null;

  // C0 controls + DEL. Kills `java\tscript:` and friends before any parsing.
  if ( /[\u0000-\u001F\u007F]/.test( href )) return null;

  // `\` is `/` to the URL parser, so `/\evil.com` === `//evil.com`.
  if ( href.includes( '\\' )) return null;

  // Protocol-relative — external, despite looking in-app. Must precede the `/` branch.
  if ( href.startsWith( '//' )) return null;

  // In-app. react-router resolves it against the app's own origin, so there is no
  // scheme to vet.
  if ( href.startsWith( '/' )) return { kind: 'internal', href };

  // Everything else must be an absolute URL whose PARSED scheme is on the list. A
  // scheme-less relative path ('orders/list') throws here and is rejected — the
  // builder asks for a rooted path, and resolving one relative to whatever route the
  // reader happens to be on is not a defensible target.
  try {
    const { protocol } = new URL( href );
    return protocol === 'http:' || protocol === 'https:' ? { kind: 'external', href } : null;
  } catch {
    return null;
  }
};

/**
 * Map a delta onto a colour tone, honouring the measure's polarity.
 *
 * `'neutral'` (the default `goodDirection`) always yields `'neutral'` — colour
 * is opt-in. And for `goodDirection: 'down'` the tone is INVERTED: a falling
 * return rate is good news, and must not be painted red.
 *
 * A zero change is `'neutral'` regardless: flat is neither good nor bad.
 */
export const deltaTone = (
  delta: KpiDelta,
  goodDirection: KpiGoodDirection = 'neutral'
): KpiDeltaTone => {
  if ( goodDirection === 'neutral' ) return 'neutral';
  if ( delta.kind === 'unknown' ) return 'neutral';
  if ( delta.absolute === 0 ) return 'neutral';

  const roseGood = goodDirection === 'up' ? delta.absolute > 0 : delta.absolute < 0;
  return roseGood ? 'positive' : 'negative';
};

/** The arrow that accompanies the delta. Shown even when the tone is neutral. */
export const deltaArrow = ( delta: KpiDelta ): 'up' | 'down' | 'flat' | null => {
  if ( delta.kind === 'unknown' ) return null;
  if ( delta.absolute > 0 ) return 'up';
  if ( delta.absolute < 0 ) return 'down';
  return 'flat';
};

/**
 * Format a KPI value for display.
 *
 * Deliberately local to this widget. The only formatter in the app
 * (`domains/accounting/utils.ts:9`) is hardcoded to `es-AR` / `ARS` and is
 * domain-local — reaching into it from a generic widget would be a layering
 * violation AND would mislabel every non-ARS number. Generalising a repo-wide
 * formatter is explicitly out of scope here.
 *
 * - `null` / non-finite -> {@link KPI_EMPTY}. An unknown value never renders as `0`.
 * - `'currency'` WITHOUT a currency code falls back to a plain number: guessing a
 *   currency is worse than not showing one (same rule as `tableData.formatCell`).
 * - `'percent'` appends `%` and does NOT multiply by 100 — Cube percent measures
 *   already arrive in percent units, so scaling here would be a 100x bug.
 */
export const formatKpiValue = (
  value: number | null | undefined,
  format: KpiFormat = 'number',
  options: { locale?: string; currency?: string; decimals?: number } = {}
): string => {
  if ( value === null || value === undefined || !Number.isFinite( value )) return KPI_EMPTY;

  const { locale, currency, decimals } = options;
  const fixed = typeof decimals === 'number' && Number.isFinite( decimals ) && decimals >= 0
    ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
    : { maximumFractionDigits: 2 };

  if ( format === 'currency' && currency ) {
    return new Intl.NumberFormat( locale, { style: 'currency', currency, ...fixed }).format( value );
  }

  const formatted = new Intl.NumberFormat( locale, fixed ).format( value );
  return format === 'percent' ? `${formatted}%` : formatted;
};

/**
 * Format the delta's PERCENTAGE — the primary readout — with an explicit sign.
 *
 * `KPI_EMPTY` for an unknown delta, and for the ÷0 case where the percentage is
 * genuinely undefined (the absolute change carries the information instead).
 */
export const formatKpiDeltaPercent = ( delta: KpiDelta, locale?: string ): string => {
  if ( delta.kind !== 'full' ) return KPI_EMPTY;

  const formatted = new Intl.NumberFormat( locale, { maximumFractionDigits: 1 }).format( Math.abs( delta.percent ));
  const sign = delta.percent > 0 ? '+' : delta.percent < 0 ? '-' : '';
  return `${sign}${formatted}%`;
};

/**
 * Format the delta's ABSOLUTE change — the secondary readout — with an explicit
 * sign, in the same units as the value itself.
 */
export const formatKpiDeltaAbsolute = (
  delta: KpiDelta,
  format: KpiFormat = 'number',
  options: { locale?: string; currency?: string; decimals?: number } = {}
): string => {
  if ( delta.kind === 'unknown' ) return KPI_EMPTY;

  const magnitude = formatKpiValue( Math.abs( delta.absolute ), format, options );
  const sign = delta.absolute > 0 ? '+' : delta.absolute < 0 ? '-' : '';
  return `${sign}${magnitude}`;
};

/**
 * Shape whatever `extractWidgetData` produced into the KPI's value + delta.
 *
 * Two possible inputs, and the shape itself tells us which:
 *  - `{ current, prior }` — the widget asked for a compare (`dateRange.compare`),
 *    so the fetch went down the `decompose()` path;
 *  - a flat `rawData()` array — no compare, so there is no delta to show.
 */
export const shapeKpiData = (
  data: CompareWidgetData | CubeRow[] | unknown,
  measure: string
): KpiData => {
  if ( isCompareWidgetData( data )) {
    const value = sumMeasure( data.current, measure );
    // `prior: []` means the prior sub-query RAN and found nothing -> `sumMeasure`
    // returns null -> the delta is UNKNOWN. That is the "+100%" trap, closed.
    const prior = sumMeasure( data.prior, measure );

    return {
      value,
      textValue: firstMeasureText( data.current, measure ),
      delta: computeKpiDelta( value, prior ),
      hasCompare: true
    };
  }

  const rows = Array.isArray( data ) ? ( data as CubeRow[]) : null;
  return {
    value: sumMeasure( rows, measure ),
    textValue: firstMeasureText( rows, measure ),
    delta: { kind: 'unknown' },
    hasCompare: false
  };
};

/**
 * The measure's value on the FIRST row, as text.
 *
 * Deliberately first-row, not summed: a text KPI reads a current-state dimension
 * (a reputation level, a status), and a KPI query carries no dimensions, so Cube
 * returns exactly one row. Summing a string is meaningless, and concatenating
 * several would be a fiction.
 *
 * `null` — never `''`, never `'—'` — when there is nothing to read. The renderer
 * owns how unknown looks.
 */
export const firstMeasureText = (
  rows: CubeRow[] | null | undefined,
  measure: string
): string | null => {
  if ( !Array.isArray( rows ) || !rows.length || !measure ) return null;

  const raw = rows[0][measure];
  if ( raw === null || raw === undefined || raw === '' ) return null;

  return String( raw );
};
